<template>
  <div class="">

    <Head />

    <div class="main lessonPlans">

      <AccesskeyLink target="accesskey_c" accesskey="c" aria-label="跳到中央內容區塊">
        :::
      </AccesskeyLink>

      <!-- 毛毛蟲 -->
      <nav aria-label="breadcrumb" class="breadcrumb_box">
        <div class="container">
          <ol class="breadcrumb">
            <li class="breadcrumb-item"><router-link to="/" :title="$t('首頁')">{{ $t('首頁') }}</router-link></li>
            <li class="breadcrumb-item active" aria-current="page">{{ $t('教案專區') }}</li>
          </ol>
        </div>
      </nav>

      <div class="lessonPlans_box">
        <div class="container">

          <div class="title_box">
            <h2>{{ $t('教案專區') }}</h2>
          </div>

          <div class="box">

            <div class="info_box" v-if="userEmail">
              <div class="user_info">
                <div class="info_item">
                  <strong>{{ $t('登入帳號：') }}</strong>{{ userEmail }}
                </div>
                <div class="info_item" v-if="maxUsageCount > 0">
                  <strong>{{ $t('剩餘可登入次數：') }}</strong>
                  <span :class="{'text-danger': remainingCount <= 5, 'text-warning': remainingCount > 5 && remainingCount <= 10}">
                    {{ remainingCount }} / {{ maxUsageCount }}
                  </span>
                </div>
              </div>
              <div class="action_info">
                <span class="countdown_text" v-if="remainingSeconds > 0">
                  {{ $t('剩餘時間：') }}{{ formatTime(remainingSeconds) }}
                </span>
                <button class="logout_btn" @click="logout">{{ $t('登出') }}</button>
              </div>
            </div>

            <div class="alert alert-info" role="alert" v-if="isLoading">
              <span>{{ $t('載入中...') }}</span>
            </div>

            <div class="alert alert-danger" role="alert" v-if="errorMessage">
              {{ errorMessage }}
            </div>

            <div v-if="!isLoading && files.length > 0">
              <div class="files_container">
                <h3 class="files_title">{{ $t('教案檔案列表') }}</h3>
                <div class="table-responsive">
                  <table class="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th scope="col" style="width: 60px;">{{ $t('#') }}</th>
                        <th scope="col">{{ $t('檔案名稱') }}</th>
                        <th scope="col" style="width: 100px;">{{ $t('副檔名') }}</th>
                        <th scope="col" style="width: 120px;">{{ $t('檔案大小') }}</th>
                        <th scope="col" style="width: 100px;">{{ $t('操作') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(file, index) in files" :key="file.fileId">
                        <td>{{ index + 1 }}</td>
                        <td class="file-name-cell" :title="file.fileName">{{ file.fileName }}</td>
                        <td>
                          <span class="badge bg-primary">{{ getFileExtension(file.fileName, file.fileExtension) }}</span>
                        </td>
                        <td>{{ formatFileSize(file.fileSize) }}</td>
                        <td>
                          <button 
                            class="btn btn-sm btn-success download-btn" 
                            @click="downloadFile(file)"
                            :title="$t('下載檔案')">
                            {{ $t('下載') }}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="alert alert-warning" role="alert" v-if="!isLoading && files.length === 0 && !errorMessage">
              {{ $t('目前沒有可下載的檔案') }}
            </div>

          </div>

        </div>
      </div>

    </div>

    <Footer />

  </div>
</template>

<script>
import Head from '@/components/Head.vue'
import Footer from '@/components/Footer.vue';
import AccesskeyLink from '@/views/AccesskeyLink.vue'

export default {
  name: 'LessonPlans',
  components: {
    Head,
    Footer,
    AccesskeyLink,
  },

  data() {
    return {
      // 使用者資訊
      userEmail: '',
      token: '',
      lpId: '',
      loginTime: '', // 登入時間
      
      // 次數資訊
      maxUsageCount: 0,
      currentUsageCount: 0,
      
      // 檔案列表
      files: [],
      
      // 計時器
      remainingSeconds: 0,
      countdownTimer: null,
      
      // 狀態
      isLoading: false,
      errorMessage: '',
    };
  },

  computed: {
    // 剩餘可登入次數
    remainingCount() {
      return Math.max(0, this.maxUsageCount - this.currentUsageCount);
    },
  },

  created() {
    // 檢查是否已登入
    this.checkLoginStatus();
  },

  beforeUnmount() {
    // 清理計時器
    this.clearCountdown();
  },

  methods: {
    // 檢查登入狀態
    checkLoginStatus() {
      const token = sessionStorage.getItem('lessonPlanToken');
      const lpId = sessionStorage.getItem('lessonPlanLpId');
      const email = sessionStorage.getItem('lessonPlanEmail');
      const loginTime = sessionStorage.getItem('lessonPlanLoginTime');
      
      if (!token || !lpId || !loginTime) {
        // 未登入，跳轉到登入頁
        this.$router.push({ name: 'LessonPlansLogin' });
        return;
      }
      
      // 檢查是否已超過 10 分鐘
      const loginDateTime = new Date(loginTime);
      const now = new Date();
      const elapsedSeconds = Math.floor((now - loginDateTime) / 1000);
      const maxSeconds = 600; // 10 分鐘
      
      if (elapsedSeconds >= maxSeconds) {
        // 已超時，自動登出
        alert(this.$t('登入時間已到，請重新登入'));
        this.logout();
        return;
      }
      
      this.token = token;
      this.lpId = lpId;
      this.userEmail = email;
      this.loginTime = loginTime;
      this.remainingSeconds = maxSeconds - elapsedSeconds;
      
      // 載入檔案列表
      this.loadFiles();
    },
    
    // 載入檔案列表
    async loadFiles() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        const apiUrl = `${this.$store.state.src}${this.$store.state.subDirectory}/ChachaLessonPlan/GetFiles`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            Token: this.token,
            LpId: parseInt(this.lpId),
            LoginTime: this.loginTime,
          }),
        });
        
        const data = await response.json();
        
        if (data.ok) {
          this.files = data.files || [];
          this.maxUsageCount = data.maxUsageCount || 0;
          this.currentUsageCount = data.currentUsageCount || 0;
          
          // 啟動倒計時
          this.startCountdown();
        } else {
          this.errorMessage = data.message || this.$t('無法載入檔案列表');
          
          // 如果是權限問題，跳轉回登入頁
          if (data.message && (data.message.includes('已過期') || 
              data.message.includes('無效') ||
              data.message.includes('已停用'))) {
            setTimeout(() => {
              this.logout();
            }, 2000);
          }
        }
      } catch (error) {
        console.error('載入檔案失敗:', error);
        this.errorMessage = this.$t('載入檔案失敗，請稍後再試');
      } finally {
        this.isLoading = false;
      }
    },
    
    // 下載檔案
    async downloadFile(file) {
      try {
        // 使用 GET 方法並將參數放在 URL 中
        const url = `${this.$store.state.src}${this.$store.state.subDirectory}/ChachaLessonPlan/DownloadFile?fileId=${file.fileId}&lpId=${this.lpId}&token=${encodeURIComponent(this.token)}&loginTime=${encodeURIComponent(this.loginTime)}`;
        
        const response = await fetch(url, {
          method: 'GET',
        });
        
        if (!response.ok) {
          throw new Error(this.$t('下載失敗'));
        }
        
        // 取得 blob 資料
        const blob = await response.blob();
        
        // 確保檔名包含副檔名
        let fileName = file.fileName;
        if (!fileName.includes('.')) {
          // 優先使用後端傳來的 fileExtension
          if (file.fileExtension) {
            fileName = fileName + '.' + file.fileExtension.toLowerCase();
          } else {
            // 如果後端沒有提供，嘗試從 Content-Type 推斷
            const contentType = response.headers.get('content-type');
            if (contentType) {
              const extension = this.getExtensionFromContentType(contentType);
              if (extension) {
                fileName = fileName + '.' + extension;
              }
            }
          }
        }
        
        // 創建下載連結
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        
        // 清理
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        
      } catch (error) {
        console.error('下載檔案失敗:', error);
        alert(this.$t('下載檔案失敗，請稍後再試'));
      }
    },
    
    // 取得檔案副檔名
    getFileExtension(fileName, fileExtension = null) {
      // 優先使用後端傳來的 fileExtension
      if (fileExtension) {
        return fileExtension.toUpperCase();
      }
      
      // 否則從檔名提取
      if (!fileName) return '';
      const parts = fileName.split('.');
      if (parts.length > 1) {
        return parts[parts.length - 1].toUpperCase();
      }
      return '';
    },
    
    // 格式化檔案大小
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '-';
      
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      
      if (i === 0) return bytes + ' ' + sizes[i];
      
      return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i];
    },
    
    // 從 Content-Type 取得副檔名
    getExtensionFromContentType(contentType) {
      const mimeTypes = {
        'application/pdf': 'pdf',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'application/vnd.ms-excel': 'xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        'application/vnd.ms-powerpoint': 'ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'text/plain': 'txt',
        'application/zip': 'zip',
        'application/x-rar-compressed': 'rar',
      };
      
      return mimeTypes[contentType] || '';
    },
    
    // 格式化時間顯示 (分:秒)
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    },
    
    // 啟動倒計時
    startCountdown() {
      // 清除之前的計時器(如果有)
      this.clearCountdown();
      
      // 不再重置時間，使用已計算的 remainingSeconds
      // this.remainingSeconds 已經在 checkLoginStatus 中計算過
      
      // 如果已經超時了，直接登出
      if (this.remainingSeconds <= 0) {
        alert(this.$t('登入時間已到，請重新登入'));
        this.logout();
        return;
      }
      
      // 每秒更新一次
      this.countdownTimer = setInterval(() => {
        this.remainingSeconds--;
        
        // 時間到了，自動登出
        if (this.remainingSeconds <= 0) {
          this.clearCountdown();
          alert(this.$t('登入時間已到，請重新登入'));
          this.logout();
        }
      }, 1000);
    },
    
    // 清除倒計時
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    
    // 登出
    async logout() {
      // 清除計時器
      this.clearCountdown();
      
      // 調用後端登出 API
      try {
        if (this.token && this.lpId && this.loginTime) {
          const apiUrl = `${this.$store.state.src}${this.$store.state.subDirectory}/ChachaLessonPlan/Logout`;
          await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              Token: this.token,
              LpId: parseInt(this.lpId),
              LoginTime: this.loginTime,
            }),
          });
        }
      } catch (error) {
        console.error('登出記錄失敗:', error);
      }
      
      // 清除登入資訊
      sessionStorage.removeItem('lessonPlanToken');
      sessionStorage.removeItem('lessonPlanLpId');
      sessionStorage.removeItem('lessonPlanEmail');
      sessionStorage.removeItem('lessonPlanLoginTime');
      
      // 跳轉到登入頁
      this.$router.push({ name: 'LessonPlansLogin' });
    },
  },
}

</script>

<style scoped>
.info_box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 5px;
  flex-wrap: wrap;
  gap: 15px;
}

.user_info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info_item {
  font-size: 0.95rem;
}

.info_item strong {
  margin-right: 5px;
}

.text-danger {
  color: #dc3545;
  font-weight: bold;
}

.text-warning {
  color: #ffc107;
  font-weight: bold;
}

.action_info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.countdown_text {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
  padding: 6px 12px;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  min-width: 100px;
  text-align: center;
}

.logout_btn {
  padding: 8px 20px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.logout_btn:hover {
  background-color: #c82333;
}

.files_container {
  margin-top: 20px;
}

.files_title {
  margin-bottom: 15px;
  font-size: 1.5rem;
  font-weight: bold;
}

.table-responsive {
  margin-top: 15px;
  overflow-x: auto;
}

.table {
  margin-bottom: 0;
}

.table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  vertical-align: middle;
  padding: 12px;
  font-weight: bold;
}

.file-name-cell {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.badge {
  padding: 4px 8px;
  font-size: 0.85rem;
}

.download-btn {
  padding: 4px 12px;
  font-size: 0.875rem;
}

.download-btn:hover {
  background-color: #28a745;
  border-color: #28a745;
}

@media (max-width: 768px) {
  .info_box {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .action_info {
    width: 100%;
    justify-content: space-between;
  }
  
  .table {
    font-size: 0.875rem;
  }
  
  .table thead th,
  .table tbody td {
    padding: 8px;
  }
  
  .file-name-cell {
    max-width: 150px;
  }
}
</style>
